import React, { FC, Fragment, useCallback, useContext, useState } from 'react';
import { Box, Paper } from '@mui/material';
import { OrderBy, OrderDirection } from 'models/search-result';
import { useQuery, NetworkStatus } from '@apollo/client';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import PageTitle from 'components/shared/page-title/page-title.component';
import ClinicPageActions from 'components/clinics/clinic-page-actions/clinic-page-actions.component';
import ClinicList from 'components/clinics/clinic-list/clinic-list.component';
import Loader from 'components/shared/loader/loader.component';
import AddClinicDialog from 'components/clinics/add-edit-clinic/add-clinic-dialog.component';
import EditClinicDialog from 'components/clinics/add-edit-clinic/edit-clinic-dialog.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { childClinicsTemplate, parentClinicTemplate } from 'apollo/templates/clinic';
import { Clinic } from 'models/clinic';
import { OrganizationListFilter } from 'models/common';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import ThreeShapeClinicAuth from 'pages/order-list/scanner-cases/auth-container/three-shape-clinic-auth.component';

const Clinics: FC = () => {
    const currentUser = currentUserVar();
    const { t: translate } = useTranslation(['navigation', 'organization']);
    const [filter, setFilter] = useState<OrganizationListFilter>({
        freeText: '',
        active: true,
    });
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [lastOffset, setLastOffset] = useState(0);
    const [orderBy, setOrderBy] = useState<OrderBy>({
        orderBy: 'name',
        orderDirection: OrderDirection.asc,
    });
    const [currentEditClinic, setCurrentEditClinic] = useState<Clinic>();
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);
    // DD-3613: a super admin impersonating from the super admin UI.
    const isImpersonating = !!currentUser.impersonatedBy;

    const {
        data: { childClinics } = {},
        loading: clinicsLoading,
        networkStatus,
        refetch,
        fetchMore,
    } = useQuery(childClinicsTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            orgId: (currentUser.organization as Clinic)?._id,
            filters: filter,
            limit: 25,
            offset: 0,
            orderBy: orderBy.orderBy,
            orderDirection: orderBy.orderDirection,
        },
        onCompleted: (data) => {
            setLastOffset(data?.childClinics?.length ?? 0);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const { data: { parentClinic } = {}, loading: parentClinicLoading } = useQuery(parentClinicTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            parentId: (currentUser.organization as Clinic)._id,
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleFetchMore = () => {
        fetchMore({
            variables: {
                offset: lastOffset,
            },
        });
    };

    const handleFilterChange = useCallback(
        debounce(({ freeText, active }: OrganizationListFilter) => {
            setLastOffset(0);
            setFilter({ freeText: freeText.toLocaleLowerCase(), active });
        }, 300),
        [],
    );

    const handleOpenAddDialog = () => {
        setIsAddDialogOpen(true);
    };

    const handleOpenEditDialog = (clinic: Clinic) => {
        setCurrentEditClinic(clinic);
        setIsEditDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setCurrentEditClinic(undefined);
        setIsAddDialogOpen(false);
        setIsEditDialogOpen(false);
    };

    const setOrderOnField = (field: string) => {
        const direction = orderBy.orderDirection === OrderDirection.asc ? OrderDirection.desc : OrderDirection.asc;
        setOrderBy({
            orderBy: field,
            orderDirection: direction,
        });
        refetch({
            filters: filter,
            offset: 0,
            orderBy: field,
            orderDirection: direction,
        });
        setLastOffset(0);
    };

    const handleClinicAdded = () => {
        setAppToasterContent({
            isOpen: true,
            severity: 'success',
            message: translate('organization:CLINIC_ADDED_MESSAGE'),
        });
        setIsAddDialogOpen(false);
        setLastOffset(0);
        refetch({ filters: filter, offset: 0, orderBy: orderBy.orderBy, orderDirection: orderBy.orderDirection });
    };

    const handleClinicModified = () => {
        setAppToasterContent({
            isOpen: true,
            severity: 'success',
            message: translate('organization:CLINIC_UPDATED_MESSAGE'),
        });
        setIsEditDialogOpen(false);
        setLastOffset(0);
        refetch({ filters: filter, offset: 0, orderBy: orderBy.orderBy, orderDirection: orderBy.orderDirection });
    };

    return (
        <Fragment>
            <PageTitle headingStyle="h1" title={translate('navigation:CLINICS')} />
            {isImpersonating &&
                customerSettings.getCustomerSetting(
                    (currentUser.organization as Clinic)?.customerSettings,
                    CONSTANTS.SETTINGS_NAMES.THREE_SHAPE_INTEGRATION,
                ) && (
                    <Box mt={2} mb={2}>
                        <Paper variant="outlined">
                            <ThreeShapeClinicAuth clinicId={(currentUser.organization as Clinic)?._id} />{' '}
                        </Paper>
                    </Box>
                )}
            <ClinicPageActions onFilterChange={handleFilterChange} onAddClinicDialogOpen={handleOpenAddDialog} />
            {(clinicsLoading && networkStatus !== NetworkStatus.fetchMore && networkStatus !== NetworkStatus.refetch) ||
            parentClinicLoading ? (
                <Loader />
            ) : (
                <ClinicList
                    parentClinic={parentClinic}
                    filteredClinics={childClinics}
                    onFetchMore={handleFetchMore}
                    onSetOrderOnField={setOrderOnField}
                    orderBy={orderBy}
                    onEditClinicClick={handleOpenEditDialog}
                />
            )}
            <AddClinicDialog
                dialogOpen={isAddDialogOpen}
                onCloseDialog={handleCloseDialog}
                onClinicAdded={() => handleClinicAdded()}
                isChildClinic
            />
            <EditClinicDialog
                dialogOpen={isEditDialogOpen}
                clinic={currentEditClinic}
                onCloseDialog={handleCloseDialog}
                onClinicModified={handleClinicModified}
            />
        </Fragment>
    );
};

export default Clinics;
